import { useLazyQuery } from '@apollo/client';
import {
  STANDARDIZE_PICKUP_ADDRESS,
  StandardizePickupAddressResponse,
  StandardizePickupAddressVariables,
} from '../graphql/queries/standardizePickupAddress';

/**
 * 揽收站点 2.0 · 地址标准化复用 hook（供站点表单 / 改地址 / AB 新建共用）。
 *
 * 调 `standardizePickupAddress`，它与建站/改址落库是**同一个后端方法**，所以返回值即最终入库地址：
 * 街道已做门牌号还原、dock 已由后端 `parseDockInfo` 剥好单独返回，前端不做任何二次加工。
 *
 * 不要退回通用的 `analyzeAddressSimple` —— 它返回的 `normalizedComponents` 是「用户输入的标准化版本」，
 * 与建站读的 `entityComponents` 不是一回事，实测站点 8690 前端显示 `167-43 Porter Rd 123 Ddb`、
 * 落库却是 `167-43 Porter Rd`。同理不要再本地跑 dock 正则，那会让前后端各持一套口径。
 */

export interface StandardizeInput {
  street: string;
  city: string;
  state: string;
  zip: string;
  unit?: string;
}

export interface StandardizeNormalized {
  /** 标准化后的街道（不含 dock），直接回填「街道」栏 */
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipcode: string;
}

export interface StandardizeResult {
  isValid: boolean;
  normalized?: StandardizeNormalized;
  coordinates?: { latitude?: number | null; longitude?: number | null };
  /** 后端从输入中剥出的 dock（运营把小号误填进街道栏时），无则 undefined */
  dock?: string;
  /** 失败原因：exception 可重试；no_match / idlog_empty 必须改地址 */
  fallbackReason?: string | null;
  addressCoreHash?: string | null;
  addressAnalysisHash?: string | null;
}

/**
 * 离线 dock 拆分：把老站点内联在 addressLine1 里的 **dock** 拆到独立 Dock 栏。
 * 仅 PickupSiteFormDrawer 回显既有站点/AB 行时使用（dock 列有值时以列为准，列为空才回退到此）。
 *
 * 关键词**只认 Dock/Door**，与后端 CooperationWarehouseApplication.DOCK_PATTERN 严格对齐。
 * 曾经多匹配了 suite|ste|unit，把**小号**当 dock 拆走：站点 9149（address_line_1='4375 E Lowell St Ste M'、
 * dock 列为 null）打开后 Ste M 显示在 Dock 栏、小号栏空；一旦保存就把小号写进 cw.dock 列，
 * 再点“重新标准化”还会拼成 `... Ste M Ste M`（line1WithDock 二次拼接）导致 core_hash 变化、命中断裂。
 * 小号语义上不是 dock（dock 是“不同站点”的判据、进 core_hash 口径），两者不能混。
 *
 * 内联的小号**不在此拆**：把它从 addressLine1 移到 addressLine2 会改变 core_hash（六字段分别参与），
 * 老订单地址将不再命中该站点。故保持原样留在街道栏、与 DB 一致，需要规范化时由运营显式剪切。
 *
 * 关键词后必须跟非字母，否则吃进街道名词根：Docks Corner→"Docks" 曾被当成 dock 剥掉。
 * 不能用 \b 收尾——那会把合法的 Dock5（无分隔）一并拒掉。
 */
export const extractDock = (addr: string): { dock?: string; stripped: string } => {
  if (!addr) return { stripped: '' };
  const m = addr.match(/\b(dock|door)(?![A-Za-z])\s*#?\s*([A-Za-z0-9-]+)\b/i);
  if (!m) return { stripped: addr.trim() };
  const dock = `${m[1]} ${m[2]}`;
  const stripped = addr.replace(m[0], '').replace(/\s{2,}/g, ' ').replace(/[,\s]+$/, '').trim();
  return { dock, stripped };
};

/** 后端 standardize_status：1 成功 2 失败 */
const STANDARDIZE_SUCCESS = 1;

/**
 * 失败原因 → i18n key。三档的处置动作不同，只说「标准化失败」运营不知道该重试还是改地址：
 * exception=地址库抖动可重试；no_match / idlog_empty=地址本身有问题，重试无用必须改。
 */
export const standardizeFailContentKey = (fallbackReason?: string | null): string => {
  switch (fallbackReason) {
    case 'exception':
      return 'standardizeFailException';
    case 'no_match':
      return 'standardizeFailNoMatch';
    case 'idlog_empty':
      return 'standardizeFailIdlogEmpty';
    default:
      return 'standardizeFailContent';
  }
};

export const useAddressStandardize = () => {
  const [run, { loading }] = useLazyQuery<StandardizePickupAddressResponse, StandardizePickupAddressVariables>(
    STANDARDIZE_PICKUP_ADDRESS,
    { fetchPolicy: 'network-only' },
  );

  const standardize = async (input: StandardizeInput): Promise<StandardizeResult> => {
    const { data } = await run({
      variables: {
        input: {
          street: input.street,
          unit: input.unit,
          city: input.city,
          state: input.state,
          zipcode: input.zip,
        },
      },
    });
    const r = data?.standardizePickupAddress;
    // 成败只看后端结论，不再从「字段是否为空」反推：地址库超时时旧接口会返回 isValid=true + 原样输入
    if (!r || r.standardizeStatus !== STANDARDIZE_SUCCESS) {
      return { isValid: false, fallbackReason: r?.fallbackReason ?? null };
    }
    return {
      isValid: true,
      normalized: {
        addressLine1: r.addressLine1WithoutDock || r.addressLine1 || input.street,
        addressLine2: r.addressLine2 ?? undefined,
        city: r.city ?? input.city,
        state: r.state ?? input.state,
        zipcode: r.zipcode ?? input.zip,
      },
      coordinates:
        r.latitude != null || r.longitude != null
          ? { latitude: r.latitude, longitude: r.longitude }
          : undefined,
      dock: r.dock ?? undefined,
      fallbackReason: null,
      addressCoreHash: r.addressCoreHash,
      addressAnalysisHash: r.addressAnalysisHash,
    };
  };

  return { standardize, loading };
};
